import React, { useCallback, useState, useMemo } from 'react';
import styled from 'styled-components';
import ReportYear from './ReportYear';
import AdminModal from './AdminModal';
import ReportList from './ReportList';
import { ReportAPIResponse } from '../api';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { Program } from '../../Reports/NewReportDialog';
import { useUserActions } from 'shared/auth/hooks/useUserActions';

interface ReportDetailsProps {
  program: Program;
  reports: ReportAPIResponse[];
  cloneExistingYear: (year: number) => unknown;
}

const year = new Date().getFullYear();
const currentYear = year.toString();
const lastYear = (year - 1).toString();
const nextYear = (year + 1).toString();
const getCurrentReports = (reports: ReportDetailsProps['reports']) => {
  let lastReport: ReportAPIResponse | undefined = undefined;
  let currentReport: ReportAPIResponse | undefined = undefined;
  let nextReport: ReportAPIResponse | undefined = undefined;
  const exclusiveReports: ReportAPIResponse[] = [];
  for (const report of reports) {
    if (report.year === lastYear) {
      lastReport = report;
      continue;
    }
    if (report.year === currentYear) {
      currentReport = report;
      continue;
    }
    if (report.year === nextYear) {
      nextReport = report;
      continue;
    }
    exclusiveReports.push(report);
  }
  return {
    lastReport,
    currentReport,
    nextReport,
    exclusiveReports,
  };
};

const StyledDiv = styled.div`
  padding-bottom: ${SPACERS.large};
  .main-reports {
    display: flex;
    gap: ${SPACERS.large};
    margin-top: ${SPACERS.medium};
    .report {
      flex: 1;
    }
  }
`;

const ReportDetails: React.FC<ReportDetailsProps> = ({
  reports,
  program,
  cloneExistingYear,
}) => {
  const { isGhosting } = useUserActions();
  const [report, setReport] = useState<ReportAPIResponse | undefined>();

  const handleOpen = useCallback(
    (r: ReportAPIResponse) => {
      setReport(r);
    },
    [setReport],
  );
  const handleClose = useCallback(() => {
    setReport(undefined);
  }, [setReport]);

  const { lastReport, currentReport, nextReport } = useMemo(
    () => getCurrentReports(reports),
    [reports],
  );

  return (
    <>
      <StyledDiv>
        <div className="main-reports">
          <ReportYear
            report={lastReport}
            label="Last Year"
            className="report"
            cardYear={year - 1}
            program={program}
            isAdminUser={isGhosting}
            handleAdminModalOpen={handleOpen}
            handleAdminModalClose={handleClose}
          />
          <ReportYear
            report={currentReport}
            label="This Year"
            className="report"
            cardYear={year}
            program={program}
            isAdminUser={isGhosting}
            handleAdminModalOpen={handleOpen}
            handleAdminModalClose={handleClose}
          />
          <ReportYear
            report={nextReport}
            label="Next Year"
            className="report"
            cardYear={year + 1}
            program={program}
            isAdminUser={isGhosting}
            handleAdminModalOpen={handleOpen}
            handleAdminModalClose={handleClose}
          />
        </div>
        <ReportList
          program={program}
          reports={reports}
          isAdminUser={isGhosting}
          handleAdminModalOpen={handleOpen}
          handleAdminModalClose={handleClose}
          cloneExistingYear={cloneExistingYear}
        />
      </StyledDiv>
      {report && (
        <AdminModal open={true} report={report} handleClose={handleClose} />
      )}
    </>
  );
};

export default ReportDetails;
